package fr.manyverse.app.presentation.components.player_screen

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ExitToApp
import androidx.compose.material.icons.automirrored.filled.TextSnippet
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.FilledTonalIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.GameTable
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.presentation.components.ZoomableImageDialog
import fr.manyverse.app.presentation.components.CharacterAvatar
import fr.manyverse.app.presentation.components.common_tabs.GameCodeCard
import fr.manyverse.app.presentation.components.common_tabs.HomeButton
import fr.manyverse.app.presentation.components.common_tabs.TableInfoCard
import fr.manyverse.app.presentation.components.common_tabs.ToolItem
import fr.manyverse.app.presentation.components.common_tabs.ToolsSection
import fr.manyverse.app.presentation.components.player_screen.character.PlayerNotesSheet
import fr.manyverse.app.presentation.viewmodel.PlayerNotesViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.character_name
import manyverse.shared.generated.resources.leave_table
import manyverse.shared.generated.resources.leave_table_confirm_action
import manyverse.shared.generated.resources.leave_table_message
import manyverse.shared.generated.resources.leave_table_title
import manyverse.shared.generated.resources.no_players_yet
import manyverse.shared.generated.resources.notes
import manyverse.shared.generated.resources.players
import manyverse.shared.generated.resources.your_character
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun PlayerTableTab(
    table: GameTable,
    tableMembers: List<TableMember>,
    characters: List<Character>,
    currentUserId: String,
    onNavigateBack: () -> Unit,
    onLeaveTable: () -> Unit,
    modifier: Modifier = Modifier
) {
    var showLeaveDialog by remember { mutableStateOf(false) }
    var showNotesSheet by remember { mutableStateOf(false) }
    val notesViewModel: PlayerNotesViewModel = koinViewModel(
        key = "notes_${table.id}_${currentUserId}"
    ) { parametersOf(table.id, currentUserId) }
    val players = remember(tableMembers, characters) {
        tableMembers.filter { member ->
            member.role == MemberRole.PLAYER &&
                    characters.any { it.userId == member.userId }
        }.sortedBy { member -> characters.find { it.userId == member.userId }?.name }
    }

    LazyColumn(
        modifier = modifier.fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        item {
            Box(modifier = Modifier.fillMaxWidth()) {
                HomeButton(
                    onNavigateBack = onNavigateBack,
                    modifier = Modifier.align(Alignment.CenterStart)
                )
                GameCodeCard(
                    code = table.code,
                    modifier = Modifier.align(Alignment.Center)
                )
                FilledTonalIconButton(
                    onClick = { showLeaveDialog = true },
                    modifier = Modifier.align(Alignment.CenterEnd),
                    colors = IconButtonDefaults.filledTonalIconButtonColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer
                    )
                ) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.ExitToApp,
                        contentDescription = stringResource(Res.string.leave_table),
                        tint = MaterialTheme.colorScheme.onErrorContainer
                    )
                }
            }
        }

        item {
            TableInfoCard(table = table)
        }

        item {
            ToolsSection {
                ToolItem(
                    painter = rememberVectorPainter(Icons.AutoMirrored.Filled.TextSnippet),
                    label = stringResource(Res.string.notes),
                    onClick = { showNotesSheet = true }
                )
            }
        }

        item {
            Text(
                text = stringResource(Res.string.players),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold
            )
        }

        if (players.isEmpty()) {
            item {
                Text(
                    text = stringResource(Res.string.no_players_yet),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        } else {
            items(players, key = { it.id }) { member ->
                val character = remember(characters, member.userId) {
                    characters.firstOrNull { it.userId == member.userId }
                }
                PlayerCard(
                    member = member,
                    character = character,
                    isCurrentUser = member.userId == currentUserId
                )
            }
        }

    }

    if (showNotesSheet) {
        PlayerNotesSheet(
            viewModel = notesViewModel,
            onDismiss = {
                notesViewModel.resetSaveState()
                showNotesSheet = false
            }
        )
    }

    if (showLeaveDialog) {
        AlertDialog(
            onDismissRequest = { showLeaveDialog = false },
            title = { Text(stringResource(Res.string.leave_table_title)) },
            text = { Text(stringResource(Res.string.leave_table_message, table.code)) },
            confirmButton = {
                TextButton(onClick = {
                    showLeaveDialog = false
                    onLeaveTable()
                }) {
                    Text(
                        text = stringResource(Res.string.leave_table_confirm_action),
                        color = MaterialTheme.colorScheme.error
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { showLeaveDialog = false }) {
                    Text(stringResource(Res.string.cancel))
                }
            }
        )
    }
}

@Composable
private fun PlayerCard(member: TableMember, character: Character?, isCurrentUser: Boolean) {
    var showAvatarFullscreen by remember { mutableStateOf(false) }

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant
        )
    ) {
        Box(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.padding(16.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                CharacterAvatar(
                    imageUrl = character?.signedImageUrl,
                    characterName = character?.name ?: member.userId,
                    size = 56.dp,
                    onClick = if (character?.signedImageUrl != null) {
                        { showAvatarFullscreen = true }
                    } else null
                )

                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    Text(
                        text = stringResource(Res.string.character_name),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = character?.name ?: member.userId,
                        style = MaterialTheme.typography.titleMedium
                    )
                }
            }

            if (isCurrentUser) {
                Icon(
                    imageVector = Icons.Default.Person,
                    contentDescription = stringResource(Res.string.your_character),
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(8.dp)
                        .size(20.dp)
                )
            }
        }
    }

    if (showAvatarFullscreen && character?.signedImageUrl != null) {
        ZoomableImageDialog(
            imageUrl = character.signedImageUrl,
            contentDescription = character.name,
            onDismiss = { showAvatarFullscreen = false }
        )
    }
}
